import { Injectable } from '@angular/core';
import { CentrosModel } from 'src/app/pages/configuracion/maestros/centros/models/centros-model';
import { EmpresasModel } from 'src/app/pages/configuracion/maestros/empresas/models/empresas-model';
import { EmpleadoCrud } from 'src/app/pages/empleados/models/empleado';
import { DesplegableData } from 'src/app/shared/components/sqp-components/sqpDesplegable/models/desplegable-data';
import { FiltroEmpleado } from 'src/app/shared/models/filtros';
import { OrdenColumna } from 'src/app/shared/models/orden-columna';

export interface EnvioSimpleBusquedaEmpleadosMetadata {
  empresas: EmpresasModel[];
  centros: CentrosModel[];
  empresasLista: DesplegableData[];
  centrosLista: DesplegableData[];
  empresasListModal: string[];
}

export interface EnvioSimpleBusquedaEmpleadosEstadoFirmante {
  empleados: EmpleadoCrud[];
  currentPage: number;
  hasMoreRows: boolean;
  filtrosEmpleados: FiltroEmpleado[];
  filtrosEmpleadosAnteriores: FiltroEmpleado[];
  ordenColumna: OrdenColumna;
  textoBuscar: string;
  codigoEmpleadoSeleccionado?: string;
}

export interface EnvioSimpleBusquedaEmpleadosEstadoGrid {
  empleados: EmpleadoCrud[];
  currentPage: number;
  hasMoreRows: boolean;
  ordenColumna: OrdenColumna;
}

export interface EnvioSimpleBusquedaEmpleadosSnapshot
  extends EnvioSimpleBusquedaEmpleadosMetadata,
    EnvioSimpleBusquedaEmpleadosEstadoFirmante {}

@Injectable({
  providedIn: 'root'
})
export class EnvioSimpleBusquedaEmpleadosCacheService {
  private metadata: EnvioSimpleBusquedaEmpleadosMetadata | null = null;
  private estadoGridSinFiltros: EnvioSimpleBusquedaEmpleadosEstadoGrid | null = null;
  private estadosPorFirmante = new Map<string, EnvioSimpleBusquedaEmpleadosEstadoFirmante>();

  tieneSnapshot(): boolean {
    return this.tieneMetadataUsable();
  }

  tieneEstadoGridSinFiltros(): boolean {
    return this.estadoGridSinFiltros !== null && this.tieneMetadataUsable();
  }

  tieneSnapshotFirmante(codigoFirmante: string): boolean {
    return this.estadosPorFirmante.has(codigoFirmante) && this.tieneMetadataUsable();
  }

  /** Metadata con empresas reales (no stub vacio tras alta sin haber abierto la lupa). */
  tieneMetadataUsable(): boolean {
    return this.metadata !== null && (this.metadata.empresas?.length ?? 0) > 0;
  }

  obtenerMetadata(): EnvioSimpleBusquedaEmpleadosMetadata | null {
    if (!this.tieneMetadataUsable()) {
      return null;
    }
    return this.clonarMetadata(this.metadata!);
  }

  obtenerEstadoGridSinFiltros(): EnvioSimpleBusquedaEmpleadosEstadoGrid | null {
    if (!this.estadoGridSinFiltros) {
      return null;
    }
    return this.clonarEstadoGrid(this.estadoGridSinFiltros);
  }

  obtenerSnapshot(): EnvioSimpleBusquedaEmpleadosSnapshot | null {
    if (!this.tieneMetadataUsable()) {
      return null;
    }
    return {
      ...this.clonarMetadata(this.metadata!),
      empleados: [],
      currentPage: 1,
      hasMoreRows: true,
      filtrosEmpleados: [],
      filtrosEmpleadosAnteriores: [],
      ordenColumna: undefined as unknown as OrdenColumna,
      textoBuscar: ''
    };
  }

  obtenerSnapshotFirmante(codigoFirmante: string): EnvioSimpleBusquedaEmpleadosSnapshot | null {
    const estado = this.estadosPorFirmante.get(codigoFirmante);
    if (!estado || !this.tieneMetadataUsable()) {
      return null;
    }
    return {
      ...this.clonarMetadata(this.metadata!),
      ...this.clonarEstadoFirmante(estado)
    };
  }

  guardarEstadoGridSinFiltros(estado: EnvioSimpleBusquedaEmpleadosEstadoGrid): void {
    this.estadoGridSinFiltros = this.clonarEstadoGrid(estado);
  }

  guardarSnapshotFirmante(codigoFirmante: string, snapshot: EnvioSimpleBusquedaEmpleadosSnapshot): void {
    this.metadata = this.clonarMetadata(snapshot);
    this.estadosPorFirmante.set(codigoFirmante, this.clonarEstadoFirmante({
      empleados: snapshot.empleados,
      currentPage: snapshot.currentPage,
      hasMoreRows: snapshot.hasMoreRows,
      filtrosEmpleados: snapshot.filtrosEmpleados,
      filtrosEmpleadosAnteriores: snapshot.filtrosEmpleadosAnteriores,
      ordenColumna: snapshot.ordenColumna,
      textoBuscar: snapshot.textoBuscar,
      codigoEmpleadoSeleccionado: snapshot.codigoEmpleadoSeleccionado
    }));
  }

  limpiar(): void {
    this.metadata = null;
    this.estadoGridSinFiltros = null;
    this.estadosPorFirmante.clear();
  }

  /**
   * Invalida listados cacheados tras alta/edicion en maestro (D8),
   * manteniendo metadata de empresas/centros y filtros por firmante.
   */
  invalidarEmpleados(): void {
    this.estadoGridSinFiltros = null;
    this.estadosPorFirmante.forEach((estado, codigo) => {
      this.estadosPorFirmante.set(codigo, {
        ...estado,
        empleados: [],
        currentPage: 1,
        hasMoreRows: true
      });
    });
  }

  /**
   * Actualiza el empleado en la cache del firmante editado (conserva busqueda/filtros).
   * En otras caches de firmante solo refresca la fila si ya existia; no inserta ni cambia la seleccion.
   * Si el firmante no tenia snapshot, lo crea con el empleado seleccionado (alta desde Editar otros datos).
   */
  actualizarEmpleadoEnCaches(empleado: EmpleadoCrud, codigoFirmante?: string): void {
    if (!empleado) {
      return;
    }
    const codigoEmpleado = empleado.codigo != null ? String(empleado.codigo).trim() : '';
    if (this.estadoGridSinFiltros) {
      this.estadoGridSinFiltros = {
        ...this.estadoGridSinFiltros,
        empleados: codigoEmpleado
          ? this.upsertEmpleadoEnLista(this.estadoGridSinFiltros.empleados, empleado)
          : this.estadoGridSinFiltros.empleados
      };
    }

    if (codigoFirmante && !this.estadosPorFirmante.has(codigoFirmante)) {
      this.asegurarEmpleadoSeleccionadoEnFirmante(codigoFirmante, empleado);
      return;
    }

    this.estadosPorFirmante.forEach((estado, codigo) => {
      const esFirmanteEditado = !!codigoFirmante && codigo === codigoFirmante;
      if (esFirmanteEditado) {
        this.estadosPorFirmante.set(codigo, {
          ...estado,
          empleados: this.upsertEmpleadoEnLista(estado.empleados, empleado),
          codigoEmpleadoSeleccionado: codigoEmpleado || estado.codigoEmpleadoSeleccionado
        });
        return;
      }
      this.estadosPorFirmante.set(codigo, {
        ...estado,
        empleados: this.reemplazarEmpleadoSiExiste(estado.empleados, empleado)
      });
    });
  }

  /**
   * Tras alta/edicion en Editar otros datos: deja al empleado listo para aparecer
   * seleccionado al reabrir empleados-page (lupa), aunque no hubiera snapshot previo.
   * No crea metadata vacia: sin empresas/centros reales la proxima apertura debe cargar APIs.
   */
  asegurarEmpleadoSeleccionadoEnFirmante(codigoFirmante: string, empleado: EmpleadoCrud): void {
    if (!codigoFirmante || !empleado) {
      return;
    }
    const codigoEmpleado = empleado.codigo != null ? String(empleado.codigo).trim() : '';
    if (!codigoEmpleado) {
      return;
    }

    const estadoActual = this.estadosPorFirmante.get(codigoFirmante);
    this.estadosPorFirmante.set(codigoFirmante, {
      empleados: this.upsertEmpleadoEnLista(estadoActual?.empleados ?? [], empleado),
      currentPage: estadoActual?.currentPage ?? 1,
      hasMoreRows: estadoActual?.hasMoreRows ?? true,
      filtrosEmpleados: estadoActual?.filtrosEmpleados ?? [],
      filtrosEmpleadosAnteriores: estadoActual?.filtrosEmpleadosAnteriores ?? [],
      ordenColumna: estadoActual?.ordenColumna ?? (undefined as unknown as OrdenColumna),
      textoBuscar: estadoActual?.textoBuscar ?? '',
      codigoEmpleadoSeleccionado: codigoEmpleado
    });

    // Solo enriquecer un grid ya cargado; no sembrar un listado de 1 fila
    // (otros firmantes reutilizan estadoGridSinFiltros y verian solo ese empleado).
    if (this.estadoGridSinFiltros) {
      this.estadoGridSinFiltros = {
        ...this.estadoGridSinFiltros,
        empleados: this.upsertEmpleadoEnLista(this.estadoGridSinFiltros.empleados, empleado)
      };
    }
  }

  private upsertEmpleadoEnLista(empleados: EmpleadoCrud[], empleado: EmpleadoCrud): EmpleadoCrud[] {
    const codigo = empleado.codigo != null ? String(empleado.codigo) : '';
    if (!codigo) {
      return [...empleados];
    }
    const index = empleados.findIndex(item => String(item.codigo) === codigo);
    if (index < 0) {
      return [{ ...empleado }, ...empleados];
    }
    const actualizada = [...empleados];
    actualizada[index] = { ...empleados[index], ...empleado };
    return actualizada;
  }

  private reemplazarEmpleadoSiExiste(empleados: EmpleadoCrud[], empleado: EmpleadoCrud): EmpleadoCrud[] {
    const codigo = empleado.codigo != null ? String(empleado.codigo) : '';
    if (!codigo) {
      return [...empleados];
    }
    const index = empleados.findIndex(item => String(item.codigo) === codigo);
    if (index < 0) {
      return [...empleados];
    }
    const actualizada = [...empleados];
    actualizada[index] = { ...empleados[index], ...empleado };
    return actualizada;
  }

  private clonarMetadata(metadata: EnvioSimpleBusquedaEmpleadosMetadata): EnvioSimpleBusquedaEmpleadosMetadata {
    return {
      empresas: [...metadata.empresas],
      centros: [...metadata.centros],
      empresasLista: metadata.empresasLista.map(item => ({ ...item })),
      centrosLista: metadata.centrosLista.map(item => ({ ...item })),
      empresasListModal: [...metadata.empresasListModal]
    };
  }

  private clonarEstadoGrid(
    estado: EnvioSimpleBusquedaEmpleadosEstadoGrid
  ): EnvioSimpleBusquedaEmpleadosEstadoGrid {
    return {
      empleados: [...estado.empleados],
      currentPage: estado.currentPage,
      hasMoreRows: estado.hasMoreRows,
      ordenColumna: estado.ordenColumna ? { ...estado.ordenColumna } : estado.ordenColumna
    };
  }

  private clonarEstadoFirmante(
    estado: EnvioSimpleBusquedaEmpleadosEstadoFirmante
  ): EnvioSimpleBusquedaEmpleadosEstadoFirmante {
    return {
      empleados: [...estado.empleados],
      currentPage: estado.currentPage,
      hasMoreRows: estado.hasMoreRows,
      filtrosEmpleados: JSON.parse(JSON.stringify(estado.filtrosEmpleados)),
      filtrosEmpleadosAnteriores: JSON.parse(JSON.stringify(estado.filtrosEmpleadosAnteriores)),
      ordenColumna: estado.ordenColumna ? { ...estado.ordenColumna } : estado.ordenColumna,
      textoBuscar: estado.textoBuscar,
      codigoEmpleadoSeleccionado: estado.codigoEmpleadoSeleccionado
    };
  }
}
